import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";

export const BR = { DEL: "Delhi (FBD)", MUM: "Mumbai" };
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const mLabel = (m) => `${MON[Number(m.slice(5, 7)) - 1]} ${m.slice(2, 4)}`;
const nf = (v, d) => Number(v).toLocaleString("en-IN", { maximumFractionDigits: d });
export const fmtL = (v, d = 2) => (v == null ? "—" : `₹${nf(v, d)} L`);
export const fmtN = (v) => (v == null ? "—" : nf(v, 2));
export const fmtP = (v) => (v == null ? "—" : `${v}%`);
export const num = (v) => (v === "" || v == null || Number.isNaN(Number(v)) ? 0 : Number(v));
export const sum = (o) => Object.values(o || {}).reduce((a, v) => a + num(v), 0);
export const r2 = (x) => Math.round(x * 100) / 100;
export const sel = "bg-white border border-slate-300 h-9 px-2 text-sm rounded-sm focus:outline-none focus:border-primary";
export const btnP = "bg-primary text-primary-foreground h-9 px-4 text-sm rounded-sm font-medium hover:brightness-95 transition-[filter] disabled:opacity-50";
export const btnS = "border border-slate-300 bg-white h-9 px-3 text-sm rounded-sm hover:border-primary transition-colors disabled:opacity-50 whitespace-nowrap";

export const STATUS = {
  none: ["Not started", "bg-white text-slate-500 border-slate-200"],
  draft: ["Draft", "bg-slate-100 text-slate-700 border-slate-300"],
  submitted: ["Submitted", "bg-sky-50 text-sky-700 border-sky-300"],
  approved: ["Approved", "bg-emerald-50 text-emerald-700 border-emerald-300"],
  returned: ["Sent back", "bg-orange-50 text-orange-700 border-orange-300"],
};
export const TONES = {
  on_track: ["On track", "bg-emerald-50 text-emerald-700 border-emerald-200", "#059669"],
  at_risk: ["At risk", "bg-amber-50 text-amber-700 border-amber-200", "#d97706"],
  behind: ["Behind", "bg-rose-50 text-rose-700 border-rose-200", "#e11d48"],
};

export const StatusChip = ({ s, tid = "bp-status-chip-badge" }) => {
  const [label, cls] = STATUS[s] || STATUS.none;
  return <span data-testid={tid} className={`inline-flex items-center text-[11px] px-2 py-0.5 border rounded-sm font-semibold uppercase tracking-wider whitespace-nowrap ${cls}`}>{label}</span>;
};

export const ToneChip = ({ t, tid }) => {
  if (!t) return <span data-testid={tid} className="text-xs text-muted-foreground">No target</span>;
  const [label, cls, hex] = TONES[t];
  return (
    <span data-testid={tid} className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 border rounded-sm font-medium whitespace-nowrap ${cls}`}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: hex }} />{label}
    </span>
  );
};

export const Meter = ({ pct, tone, tid }) => (
  <div data-testid={tid} className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
    <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${Math.min(pct || 0, 100)}%`, background: (TONES[tone] || [])[2] || "#94a3b8" }} />
  </div>
);

export const useBpMeta = (fy) => {
  const [m, setM] = useState(null);
  useEffect(() => {
    api.get("/bp/meta", { params: { fy } }).then((r) => setM(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [fy]);
  return m;
};

export const FySelect = ({ meta, value, onChange }) => (
  <select data-testid="bp-fy-select-dropdown" className={sel} value={value} onChange={(e) => onChange(e.target.value)}>
    {meta.fys.map((f) => <option key={f} value={f}>FY {f}{f === meta.current_fy ? " (this year)" : ""}</option>)}
  </select>
);

export const Section = ({ title, hint, children, tid, right }) => (
  <section data-testid={tid} className="bg-white border border-slate-300 rounded-sm ax-fade min-w-0">
    <div className="flex flex-wrap items-end justify-between gap-2 px-4 pt-4 pb-3 border-b border-slate-200">
      <div>
        <h3 className="text-base font-semibold tracking-tight text-slate-900">{title}</h3>
        {hint && <p className="text-xs text-slate-500 mt-0.5">{hint}</p>}
      </div>
      {right}
    </div>
    <div className="p-4">{children}</div>
  </section>
);

export const History = ({ rows }) => (
  <Section title="Change history" tid="bp-history">
    {!rows?.length ? <p className="text-sm text-slate-500">No changes yet.</p> : (
      <ol className="space-y-2 text-sm">
        {[...rows].reverse().slice(0, 15).map((h, i) => (
          <li key={`${h.at}-${i}`} className="flex gap-3" data-testid={`bp-history-row-${i}`}>
            <span className="num text-xs text-slate-500 w-36 shrink-0">{new Date(h.at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" })}</span>
            <span><b className="font-medium">{h.action}</b> · {h.by}{h.note ? <span className="text-slate-600"> — “{h.note}”</span> : null}</span>
          </li>
        ))}
      </ol>
    )}
  </Section>
);

export const Tabs = ({ value, onPick, items, tid }) => (
  <div className="inline-flex border border-slate-300 rounded-sm overflow-hidden max-w-full overflow-x-auto" data-testid={tid}>
    {items.map(([k, label]) => (
      <button key={k} type="button" data-testid={`${tid}-${k}`} onClick={() => onPick(k)}
        className={`h-9 px-4 text-sm whitespace-nowrap transition-colors ${value === k ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>{label}</button>
    ))}
  </div>
);

export const evenSpread = (total, months) => Object.fromEntries(months.map((m) => [m, r2(total / months.length)]));
export const likeLy = (total, ly, months) => {
  const base = sum(ly);
  return base ? Object.fromEntries(months.map((m) => [m, r2((total * num(ly[m])) / base)])) : evenSpread(total, months);
};
export const grow = (ly, months, pct) => Object.fromEntries(months.map((m) => [m, r2(num(ly[m]) * (1 + pct / 100))]));
